import {
  useCreateSlackAndEmailOutboundIntegration,
  useUpdateSlackAndEmailOutboundIntegration,
} from '@modules/subscribe/hooks';
import { isOutboundIntegrationForChannelType } from '@modules/subscribe/utils/isOutboundIntegrationForChannelType';
import {
  ReportingIntegrationViewFragment,
  SlackChannelInput,
} from 'generated/graphql';
import { IntegrationChannelType } from 'generated/graphql';
import { UUID } from 'v2/core/entities/uuid';
import { isEqual } from 'v2/core/utils/set';

interface UseUpdateSubscriptionOptions {
  integrations: ReportingIntegrationViewFragment[];
  outbounds: UUID[];
}

export const useUpdateSubscription = ({
  integrations,
  outbounds,
}: UseUpdateSubscriptionOptions) => {
  const {
    createOutboundIntegration,
    isLoading: isCreatingOutboundIntegration,
  } = useCreateSlackAndEmailOutboundIntegration();

  const {
    updateOutboundIntegration,
    isLoading: isUpdatingOutboundIntegration,
  } = useUpdateSlackAndEmailOutboundIntegration();

  const createOrUpdateOutboundIntegration = async (
    channelType: IntegrationChannelType,
    slackChannels: SlackChannelInput[],
    emails: string[],
    outboundIDsToAdd: Set<UUID>,
  ) => {
    const currentOutbound = integrations.find((integration) =>
      isOutboundIntegrationForChannelType(
        integration.channel.type,
        integration.direction,
        channelType,
      ),
    );

    if (currentOutbound) {
      await updateOutboundIntegration(
        channelType,
        currentOutbound.id,
        slackChannels,
        emails,
      );
      return;
    }

    const createdOutboundID = await createOutboundIntegration(
      channelType,
      slackChannels,
      emails,
    );

    if (createdOutboundID) {
      outboundIDsToAdd.add(createdOutboundID);
    }
  };

  const updateSubscription = async (
    updateMutationCallback: (outboundIDs: UUID[]) => Promise<unknown>,
    slackChannels: SlackChannelInput[],
    emails: string[],
    shouldAlwaysMakeMutationCall?: boolean,
  ) => {
    const outboundIDsToAdd = new Set(outbounds);

    await Promise.all([
      createOrUpdateOutboundIntegration(
        IntegrationChannelType.Slack,
        slackChannels,
        emails,
        outboundIDsToAdd,
      ),
      createOrUpdateOutboundIntegration(
        IntegrationChannelType.Email,
        slackChannels,
        emails,
        outboundIDsToAdd,
      ),
    ]);

    if (
      !isEqual(new Set(outbounds), outboundIDsToAdd) ||
      shouldAlwaysMakeMutationCall
    ) {
      await updateMutationCallback([...outboundIDsToAdd]);
    }
  };

  return {
    updateSubscription,
    isLoading: isCreatingOutboundIntegration || isUpdatingOutboundIntegration,
  };
};
